<template lang="pug">
  mega.shop-mega(tab-name="Shop by Category" tabindex="0")
    mega-panel.top-level(
      v-for="cat in primaryMenu"
      :key="cat.url"
      :url="cat.url"
      :title="__(cat.name, 'categories')"
      :category-id="cat.id"
    )
      .category-panel(v-bind="cat.url === '/deals' ? {class: 'deals'} : undefined" v-if="cat.children")
        div
          .l1-head
            rogue-link.title(:to="cat.url" :categoryId="cat.id" :class="FLAT[cat.id] && 'title-flat'" :aria-label="`Link to ${cat.name}`")
              | {{__(cat.name, 'categories')}}
              //- Decorative: the name is right next to it, so alt text would make screen readers read it twice
              StaticImage.title-img(v-if="FLAT[cat.id] && cat.image" :src="cat.image" alt="" :width="128" :height="128" ssr lazy)
          .children(:class="FLAT[cat.id] && 'flat'")
            mega-panel.child-menu(
              v-for="child in cat.children"
              :key="child.url"
              :url="child.url"
              :title="__(child.name, 'categories')"
              :category-id="child.id"
              :image="FLAT[cat.id] ? undefined : child.image"
            )
              rogue-link.cat-link(
                v-for="grand in child.children"
                :key="grand.url"
                :to="grand.url"
                :categoryId="grand.id"
                :aria-label="`Link to ${grand.name}`"
              )
                | {{__(grand.name, 'categories')}}
                .arrow
        .right-image-container(v-if="cat.right_image")
          rogue-link.right-img-link(:to="cat.right_link" :aria-label="`Link to ${cat.name}`")
            StaticImage.flat-img(:src="cat.right_image" :alt="cat.name" :width="650" :height="510" ssr lazy background="#E9E9E9")
    .mobile-links
      rogue-link(
        v-for='link in mobileMenuLinks'
        :key='link.url'
        :to='link.url'
        :aria-label="`Link to ${link.name}`"
      )
        StaticImage(
          v-if="link.img"
          style="height: 42px; width: 186px"
          :src="link.img.src"
          :width="link.img.width"
          :alt="`${__(link.name, 'categories')}`"
          :height="link.img.height"
          ssr
        )
        span(v-else) {{ __(link.name, 'categories') }}
</template>
<script setup lang="ts">
import { onMounted, onServerPrefetch, ref } from 'vue'
import { useCategoryStore } from '@/js/stores/categoryStore'
import MegaPanel from '@/js/components/header/mega-panel.vue'
import Mega from '@/js/components/header/mega.vue'
import { useRogue } from '@/js/services/Rogue'

type MenuLink = {
  name: string,
  url: string,
  img?: { src: string, width: number, height: number },
}

type MenuCategory = {
  id: number,
  name: string,
  url: string,
  sort?: number,
  parent?: number,
  type?: string,
  image?: string,
  column?: string,
  depth?: number,
  children?: MenuCategory[],
  right_image?: string,
  right_link?: string,
}

const MENU_SORT = [
  4667, 4721, 7967, 4947, 5001, 5091, 5145, 5191, 5229, 6271, 4863, 5063, 6005, 6043, 6053, 14254, 14043, // top level
  14591, 4733, 4723, // Second Level Strength Equipment
  8193, 8203, 8183, 8173, 8213, // Second level rigs racks
  4973, // Second level Conditioning
  9859, 5173, // Second level Gear & Accessories
  14567, // Second level Iron Game Programming
  14592, 14593, 14292, // Third level Functional Trainers
  4755, 4757, 4759, 4761, 14560, 7651, 13000, 4765, // Third level - Strength Training
  4979, 4975, 4977, 8081, 14749, 14598, // Third Level - Endurance
  5095, 14250, // Third level - Men's Apparel
  5107, 8383, 14251, 5109, 5111, 5113, // Third level - Women's Apparel
  5125, // Third level - Headwear
  5069, 5071, 5073, 5067, 5075, // Shoes > Weightlifting Shoes
  14210, 14253, 14247, 9088, 5087, 10433, 5089, // Shoes > Training Shoes
  14632, 14637, 14641, 14647, 14648, 14764, // Replacement Parts
]

const FLAT: Record<number, boolean> = {
  4863  : true, // Equipment
  6043  : true, // Facility Outfitting
  14254 : true, // Iron Game Programming
  14632 : true, // Replacement parts
}

const rogue = useRogue()
const categoryStore = useCategoryStore()

const mobileMenuLinks = ref<MenuLink[]>([])
const primaryMenu = ref<MenuCategory[]>([])

async function loadMenu() {
  const menuData = await categoryStore.loadMenu(rogue)

  const overwriteCategoryNames: Record<string, string> = {
    'Equipment for CrossFit®'  : 'CrossFit® Equipment',
    'Concept 2 Rowers'         : 'Rowers',
    'Cable Pulley Accessories' : 'Cable Attachments',
  }

  const categories: MenuCategory[] = Object.values(menuData)
    .map(menuItem => {
      const { accessories, url, id } = menuItem
      let parent = menuItem.parent

      const cablePulleyAttachementsCatId = 14292
      if (id === cablePulleyAttachementsCatId) {
        // Shift the parent to Functional Trainers in the menu
        parent = 14591
      }

      const name = overwriteCategoryNames[menuItem.name] ? overwriteCategoryNames[menuItem.name] : menuItem.name
      const retval: MenuCategory = {
        sort   : MENU_SORT.includes(id) ? MENU_SORT.indexOf(id) : 9999,
        url    : `${url[0] !== '/' ? '/' : ''}${url.replace(/\.php/g, '')}`,
        id,
        name,
        parent,
        type   : 'menu',
        image  : menuItem.navigation_image,
        column : menuItem.column !== '' ? menuItem.column : undefined,
      }

      Object.assign(retval, accessories || {})

      return retval
    })
    .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))

  const rootCategoryId = categories.find(category => category.parent === 1 && category.id !== 2)?.id

  const addChildren = (category: MenuCategory, depth = 0) => category.children = categories
    .filter(c => c.parent === category.id)
    .map(c => ({
      ...c,
      depth,
    }))
  const menu = categories.filter(c => c.parent === rootCategoryId)

  menu.forEach(cat => cat.depth = 0)

  menu.forEach(topLevel => {
    addChildren(topLevel, 1)
    topLevel.children?.forEach(child => addChildren(child, 2))
  })

  menu.unshift({ url: '/deals', id: -1, name: 'Deals' })

  primaryMenu.value = menu
}

function load() {
  return Promise.all([
    loadMenu(),
    import(/* webpackChunkName: "ssr-header-static" */`@/assets/top-nav/${rogue.site.code.toLowerCase()}.ts`)
      .then(({ mobileMenuLinks: links }) => mobileMenuLinks.value = links),
  ])
}

onServerPrefetch(() => load())

onMounted(() => {
  load()
})
</script>
<style lang="scss">
.shop-mega {
  a {
    position: relative;
    font-size: $size-xs;
    color: $color-black;
    text-decoration: none;
    justify-content: space-between;
    align-items: center;
    white-space: pre-wrap;
    display: flex;
  }

  .cat-link .arrow {
    pointer-events: none;
  }

  .mobile-links {
    background-color: $color-gray-light;
  }

  @include all-desktop() {
    .mobile-links, .menu-item.deals {
      display: none;
    }

    .panel {

      a {
        margin-top: $space-xs;

        &:hover {
          color: $color-red;
        }
      }

      .l1-head {
        margin-bottom: 0;
        font-family: "Roboto Condensed Header", Helvetica, Arial, sans-serif;
        line-height: 1;
        text-transform: uppercase;
        font-weight: bold;

        a {
          margin-top: 0;
        }

        .title-flat {
          display: block
        }
      }
    }

    .category-panel {
      padding: $space-m;
      background-color: $color-gray-light;
      height: 100%;
      width: 100%;
      box-shadow: 0 4px 5px -2px rgba(0, 0, 0, 0.2);
      display: flex;
      justify-content: center;
      flex-direction: row;
      max-width: 100%;

      .right-image-container {
        width: 630px;

        a {
          margin-top: 0;
        }
      }
    }

    .child-menu {
      width: 128px;
      margin-right: $space-m;

      a {
        font-size: $size-xs;
        white-space: pre-wrap;
        margin-top: $space-xs;
      }

      .menu-link {
        a {
          font-size: $size-s;
          font-weight: $weight-bold;
          font-family: $font-condensed;
          padding: 0;
          white-space: pre-wrap;
          text-transform: uppercase;
          line-height: normal;

          &:hover {
            color: $color-red;
          }
        }
      }

      .icon.arrow {
        display: none;
      }
    }

    .children {
      display: flex;
      flex-wrap: nowrap;
      flex-direction: row;

      &.flat {
        flex-direction: column;
        width: 260px;
        flex-shrink: 0;

        .flat-img {
          display: block;
        }

        .menu-item {
          margin-top: $space-s;
          margin-right: 0;
          width: 256px;
        }
      }
    }
  }

  @include not-desktop() {
    .right-image-container {
      display: none;
    }
  }

  .right-img {
    flex-shrink: 0;
    flex-grow: 0;
  }

  .right-img-link {
    display: block;
  }

  .title-img {
    display: block;
    width: 128px;
    height: 128px;
    margin-top: 10px;
    box-shadow: 0 0 10px 1px #A2A2A2;
  }

  .title {
    white-space: nowrap;
    font-size: 18px;
    margin: 0;

    &.active {
      color: $color-black;
    }
  }

  @include not-desktop() {
    .l1-head {
      display: none;
    }
    // @todo should probably be shared with the Mega Panel link class...
    a {
      color: $color-black;
      line-height: 48px;
      margin: 0;
      padding: 0 $space-s;
      border-bottom: 1px solid $color-gray-light;
    }
  }
}
</style>
